import React, { useState, useMemo } from "react";
import {
  LightModeOutlined,
  DarkModeOutlined,
  ArrowDropDownOutlined,
} from "@mui/icons-material";
import FlexBetween from "components/FlexBetween";
import NotificationBell from "components/NotificationBell";
import LanguageSwitcher from "components/LanguageSwitcher";
import { useDispatch } from "react-redux";
import { setMode } from "state";
import {
  AppBar,
  Button,
  Box,
  Divider,
  Typography,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import profileImage from "assets/profile.jpeg";
import { capitalizeFirstLetter, logout } from "utils";
import { NAV_PAGES } from "utils/pagePermissions";

const Navbar = ({ user, isSidebarOpen, setIsSidebarOpen, breadcrumb }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSmall = useMediaQuery("(max-width: 480px)");

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  // Falls back to a 2-level "Group › Page" trail derived from the same nav
  // config that drives the Sidebar, unless a page registered its own
  // (deeper / dynamic) trail via useSetBreadcrumb.
  const defaultTrail = useMemo(() => {
    const page = NAV_PAGES.find((p) => `/${p.slug}` === pathname);
    return page ? [{ label: page.group }, { label: page.label }] : null;
  }, [pathname]);

  const trail = breadcrumb || defaultTrail;

  const frameSx = {
    height: "38px",
    borderRadius: "10px",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": { borderColor: theme.palette.secondary.light },
  };

  return (
    <AppBar sx={{
      position: "sticky",
      top: 0,
      zIndex: theme.zIndex.appBar,
      background: theme.palette.background.default,
      boxShadow: "none",
      borderBottom: `1px solid ${theme.palette.divider}`,
    }}>
      <Toolbar sx={{ justifyContent: "space-between", gap: "16px" }}>
        {/* LEFT */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", minWidth: 0, flex: 1 }}>
          <IconButton
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            sx={{
              ...frameSx,
              width: "38px",
              flex: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "3.5px",
            }}
          >
            <Box component="span" sx={{ width: "16px", height: "1.8px", borderRadius: "2px", background: theme.palette.secondary.dark }} />
            <Box component="span" sx={{ width: "11px", height: "1.8px", borderRadius: "2px", background: theme.palette.secondary.dark, alignSelf: "flex-start", ml: "9px" }} />
            <Box component="span" sx={{ width: "16px", height: "1.8px", borderRadius: "2px", background: theme.palette.secondary.dark }} />
          </IconButton>

          {trail && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px", fontSize: "13px", flexWrap: "wrap", minWidth: 0, overflow: "hidden" }}>
              {trail.map((item, i) => {
                const isLast = i === trail.length - 1;
                return (
                  <Box key={i} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "7px" }}>
                    {i > 0 && <Box component="span" sx={{ color: theme.palette.text.disabled }}>›</Box>}
                    {item.to && !isLast ? (
                      <Box
                        component="span"
                        onClick={() => navigate(item.to)}
                        sx={{ color: theme.palette.secondary.main, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}
                      >
                        {item.label}
                      </Box>
                    ) : (
                      <Box
                        component="span"
                        sx={{
                          color: isLast ? theme.palette.text.primary : theme.palette.text.secondary,
                          fontWeight: isLast ? 700 : 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.label}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* RIGHT */}
        <FlexBetween gap="0.5rem" sx={{ flex: "none" }}>
          <IconButton onClick={() => dispatch(setMode())} sx={{ ...frameSx, width: "38px" }}>
            {theme.palette.mode === "dark"
              ? <DarkModeOutlined sx={{ fontSize: "20px" }} />
              : <LightModeOutlined sx={{ fontSize: "20px" }} />}
          </IconButton>

          <Box sx={{ ...frameSx, display: "flex", alignItems: "center", px: "4px" }}>
            <LanguageSwitcher />
          </Box>
          <Box sx={{ ...frameSx, width: "38px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <NotificationBell />
          </Box>

          <FlexBetween>
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                ...frameSx,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textTransform: "none",
                gap: "0.75rem",
                pl: "14px",
                pr: "5px",
                height: "44px",
              }}
            >
              {!isSmall && (
                <Box textAlign="left">
                  <Typography fontWeight="bold" fontSize="0.8125rem" lineHeight={1.2} sx={{ color: theme.palette.text.primary }}>
                    {capitalizeFirstLetter(user.name)}
                  </Typography>
                  <Typography fontSize="0.65625rem" sx={{ color: theme.palette.secondary[300] }}>
                    {capitalizeFirstLetter(
                      user.role !== "agent" ? user.role : `${user.role} - ${user.agentId}`
                    )}
                  </Typography>
                </Box>
              )}
              <Box
                component="img"
                alt="profile"
                src={user.profilePicture || profileImage}
                height="34px"
                width="34px"
                borderRadius="9px"
                sx={{ objectFit: "cover" }}
                onError={(e) => { e.target.src = profileImage; }}
              />
              <ArrowDropDownOutlined sx={{ color: theme.palette.secondary[300], fontSize: "20px", mr: "4px" }} />
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <MenuItem onClick={() => { setAnchorEl(null); navigate("/profile"); }}>My Profile</MenuItem>
              <Divider />
              <MenuItem onClick={() => logout()}>Log Out</MenuItem>
            </Menu>
          </FlexBetween>
        </FlexBetween>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
